/*
  Lays each flavour's sticker wrapper exactly over that flavour's
  product photo.

  The stickers render outside the cards so nothing can clip them, but
  they still belong to a product. Measuring each photo into
  `--sh-hs-<flavour>-x/y/w/h` on the section lets the sticker rules go
  on using percentages of the card, as they did when they lived inside
  it. Re-measured whenever the row can have moved.
*/
(function () {
  function place(section) {
    var base = section.getBoundingClientRect();

    section.querySelectorAll('.sh-carousel__card').forEach(function (card) {
      var match = /sh-carousel__card--([A-Za-z0-9_-]+)/.exec(card.className);
      if (!match) return;

      // The photo, not the whole card: the caption underneath is not
      // what the stickers were ever placed against.
      var box = card.querySelector('.sh-carousel__photo') || card;
      var r = box.getBoundingClientRect();
      if (!r.width && !r.height) return;

      var fl = match[1];
      section.style.setProperty('--sh-hs-' + fl + '-x', (r.left - base.left) + 'px');
      section.style.setProperty('--sh-hs-' + fl + '-y', (r.top - base.top) + 'px');
      section.style.setProperty('--sh-hs-' + fl + '-w', r.width + 'px');
      section.style.setProperty('--sh-hs-' + fl + '-h', r.height + 'px');

      keepClear(section, fl, r);
    });
  }

  /*
    The stickers may be placed anywhere across the section, which means
    a setting can land one on the can itself. Any sticker overlapping
    the photo is pushed sideways until it clears, towards whichever
    edge is nearer, so the product is never covered.
  */
  var GAP = 12;
  var BLEED = 0.25;      // share of a sticker that may sit off the section edge
  var MAX_BLEED = 48;    // ...and never more than this many pixels

  function keepClear(section, fl, photo) {
    var wrap = section.querySelector('.sh-carousel__stickers--' + fl);
    if (!wrap) return;

    var base = section.getBoundingClientRect();

    wrap.querySelectorAll('.sh-carousel__hover-sticker').forEach(function (st) {
      /* Measure where it would sit with no nudge at all. The stickers
         transition their transform, so the transition comes off first:
         otherwise the rect read back is a half-finished animation and
         the push comes out short. */
      var transition = st.style.transition;
      st.style.transition = 'none';
      st.style.setProperty('--sh-hs-avoid', 'translate(0, 0)');

      var r = st.getBoundingClientRect();
      if (!r.width) { st.style.transition = transition; return; }

      var overlaps = r.right > photo.left && r.left < photo.right &&
                     r.bottom > photo.top && r.top < photo.bottom;

      // How far it may hang off the section edge: enough to look
      // placed by hand, not enough to disappear off the screen.
      var bleed = Math.min(r.width * BLEED, MAX_BLEED);

      function settle(push) {
        var l = r.left + push;
        var rt = r.right + push;
        // Bring it back inside the section if this push threw it out.
        if (l < base.left - bleed) push += (base.left - bleed) - l;
        else if (rt > base.right + bleed) push -= rt - (base.right + bleed);

        l = r.left + push;
        rt = r.right + push;
        return {
          push: push,
          clear: rt <= photo.left - GAP || l >= photo.right + GAP
        };
      }

      var push = 0;

      if (overlaps) {
        // Out to the left, or out to the right: the shorter trip wins,
        // unless that side leaves it half off the screen.
        var left = settle(photo.left - GAP - r.right);
        var right = settle(photo.right + GAP - r.left);
        var first = Math.abs(left.push) <= Math.abs(right.push) ? left : right;
        var other = first === left ? right : left;
        push = first.clear ? first.push : (other.clear ? other.push : first.push);
      } else {
        push = settle(0).push;
      }

      if (push) {
        st.style.setProperty('--sh-hs-avoid', 'translate(' + Math.round(push) + 'px, 0)');
      }

      // Settle the new transform before the transition goes back on,
      // so the correction does not animate across the screen.
      void st.offsetWidth;
      st.style.transition = transition;
    });
  }

  function init(scope) {
    var root = scope || document;
    root.querySelectorAll('[class*="sh-carousel-"].sh-section').forEach(function (section) {
      if (!section.querySelector('.sh-carousel__stickers')) return;
      if (section.dataset.shStickersReady) return;
      section.dataset.shStickersReady = 'true';

      var measure = function () { place(section); };
      measure();

      // The row scrolls, the window resizes, images arrive late, and
      // the cards size themselves from the viewport.
      var rail = section.querySelector('[data-sh-rail]');
      if (rail) rail.addEventListener('scroll', measure, { passive: true });
      window.addEventListener('resize', measure, { passive: true });
      window.addEventListener('load', measure);

      if ('ResizeObserver' in window) {
        var ro = new ResizeObserver(measure);
        ro.observe(section);
        section.querySelectorAll('.sh-carousel__photo').forEach(function (p) { ro.observe(p); });
      }

      section.querySelectorAll('.sh-carousel__photo img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', measure, { once: true });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
